import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { BuyerAnalyticsPanel } from "@/components/app/BuyerAnalyticsPanel";
import { ArrowRight, Sparkles } from "lucide-react";

export default function BuyerDashboard() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sourcing/requests").then(({data}) => setRequests(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Buyer workspace</div>
          <h1 className="page-title">Welcome, {user?.full_name?.split(" ")[0]}</h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">Describe a need, review AI-extracted requirements, and manage every RFQ in one place.</p>
        </div>
        <Link to="/buyer/new" className="btn-gold self-start" data-testid="new-request-cta">
          <Sparkles size={16}/> New sourcing request
        </Link>
      </div>

      <BuyerAnalyticsPanel />

      <div className="surface">
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-200">
          <div className="section-title">Recent sourcing requests</div>
          <Link to="/buyer/requests" className="text-sm font-medium text-[hsl(var(--navy))] hover:underline" data-testid="see-all-requests">View all →</Link>
        </div>
        {loading ? (
          <div className="p-5 space-y-3">{[...Array(3)].map((_,i)=>(<div key={i} className="skeleton h-14"/>))}</div>
        ) : requests.length === 0 ? (
          <div className="p-10 text-center">
            <div className="text-lg font-semibold tracking-tight">No requests yet</div>
            <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">Start by describing what you need to source. The AI will structure your requirements and prepare a professional RFQ.</p>
            <Link to="/buyer/new" className="btn-gold mt-5 inline-flex" data-testid="empty-new-request-btn">Start first request <ArrowRight size={14}/></Link>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {requests.slice(0, 6).map((r) => (
              <li key={r.id}>
                <Link to={`/buyer/requests/${r.id}`} className="block px-5 py-4 hover:bg-slate-50 transition-colors" data-testid={`request-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{r.requirements?.product_or_service || "Untitled request"}</div>
                      <div className="text-xs text-slate-500 mt-1 line-clamp-1">{r.original_text}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs uppercase tracking-wider text-slate-500">{r.status.replace(/_/g,' ')}</div>
                      <div className="text-[11px] text-slate-400 mt-1 font-mono" data-selectable>{r.id.slice(0,8)}</div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
